import { useEffect, useRef } from 'react';

/**
 * Custom hook to detect clicks outside of a component
 * Useful for closing menus, modals, dropdowns, etc.
 * 
 * @param {Function} handler - Callback function to execute when click outside is detected
 * @param {Array} excludeRefs - Additional refs to exclude from outside click detection
 * 
 * @example
 * const menuRef = useClickOutside(() => setIsOpen(false));
 * return <div ref={menuRef}>Menu content</div>
 */
export const useClickOutside = (handler, excludeRefs = []) => {
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside the main ref
      if (ref.current && !ref.current.contains(event.target)) {
        // Check if click is inside any excluded refs
        const isExcluded = excludeRefs.some(
          (excludeRef) =>
            excludeRef.current && excludeRef.current.contains(event.target)
        );

        // If not excluded, call the handler
        if (!isExcluded) {
          handler(event);
        }
      }
    };

    // Add event listener
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    // Cleanup
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [handler, excludeRefs]);

  return ref;
};
